<template>
  <UPage class="flex-1 flex flex-col min-h-0" :ui="{ center: 'flex flex-col flex-1 min-h-0' }">
    <UPageHeader title="Projekte" :description="tenantName ? `Mandant: ${tenantName}` : undefined">
      <template #links>
        <UButton
          to="/microtech-hub/addresses"
          color="neutral"
          icon="i-heroicons-arrow-left"
          label="Zurück"
          variant="ghost"
        />
        <UButton
          color="neutral"
          icon="i-heroicons-cog-6-tooth"
          label="Spalten"
          variant="outline"
          @click="openColumnSelector"
        />
        <UButton
          :loading="loading"
          color="primary"
          icon="i-heroicons-arrow-path"
          label="Aktualisieren"
          @click="() => fetchProjects(false)"
        />
      </template>
    </UPageHeader>

    <UPageBody class="p-0 sm:p-0 flex flex-col min-h-0 h-full">
      <UTabs
        v-model="currentTabIndex"
        :items="tabItems"
        :ui="{ content: 'flex-1 min-h-0 flex flex-col' }"
        class="flex flex-col h-full min-h-0"
      >
        <template #content>
          <PortalTable
            ref="portalTable"
            :all-columns="allPossibleColumns"
            :columns-loading="loading && allPossibleColumns.length === 0"
            :data="projects"
            :loading="loading"
            :selected-columns="selectedColumns"
            :sort-storage-key="SORT_STORAGE_KEY"
            :total="totalCount"
            empty-description="Es konnten keine Projekte für diesen Mandanten geladen werden."
            empty-icon="i-heroicons-folder-open"
            empty-title="Keine Projekte gefunden"
            class="h-full"
            @load-more="fetchProjects(true)"
            @reset:columns="resetToDefault"
            @select="onRowClick"
            @update:columns="updateColumns"
          />
        </template>
      </UTabs>
    </UPageBody>
  </UPage>
</template>

<script lang="ts" setup>
import { computed, onMounted, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import axios from 'axios'
import PortalTable from '@/components/PortalTable.vue'
import { useProjectStore } from '@/stores/project'
import { fetchTableFields } from '@/utils/graphql-introspection'
import { useAuthStore } from '@/stores/auth'

const authStore = useAuthStore()
const projectStore = useProjectStore()
const router = useRouter()

const customerNo = computed(() => authStore.userInfo?.activeTenant?.customerNo || '')
const tenantName = computed(() => authStore.userInfo?.activeTenant?.tenantName || '')

const loading = ref(false)
const projects = ref<Record<string, unknown>[]>([])
const allPossibleColumns = ref<Array<{ key: string; label: string; graphql?: string }>>([])
const selectedColumns = ref<
  Array<{ key: string; label: string; locked?: boolean; graphql?: string }>
>([])
const portalTable = ref<InstanceType<typeof PortalTable> | null>(null)

const endCursor = ref('')
const hasNextPage = ref(false)
const limit = ref(1000)
const totalCount = ref(0)
const currentTabIndex = ref('open')

const tabItems = [
  { label: 'Offen', value: 'open' },
  { label: 'Archiv', value: 'archive' },
]

const STORAGE_KEY = computed(
  () =>
    `cc_prj_cols_${customerNo.value}_${tenantName.value}_${currentTabIndex.value === 'archive' ? 'archive' : 'open'}`,
)
const SORT_STORAGE_KEY = computed(
  () =>
    `cc_prj_sort_${customerNo.value}_${tenantName.value}_${currentTabIndex.value === 'archive' ? 'archive' : 'open'}`,
)

async function fetchMetadata() {
  if (!customerNo.value || !tenantName.value) return
  try {
    const isArchive = currentTabIndex.value === 'archive'
    const rootField = isArchive ? 'tblProjectsArchive' : 'tblProjects'

    // Attempt to fetch fields for the respective table. If it fails or returns empty, use defaults.
    const fields = await fetchTableFields(rootField, customerNo.value, tenantName.value).catch(
      () => [],
    )

    const columns: Array<{ key: string; label: string; graphql?: string }> = []
    for (const f of fields) {
      if (
        f.name.startsWith('_') ||
        f.name.endsWith('_int') ||
        f.name.startsWith('tbl') ||
        f.name.startsWith('lnk')
      )
        continue
      columns.push({
        key: f.name,
        label: f.label || f.description || f.name,
        graphql: f.graphql,
      })
    }

    allPossibleColumns.value = columns

    // Update selected columns labels from metadata
    if (selectedColumns.value.length > 0) {
      selectedColumns.value = selectedColumns.value.map((sc) => {
        const found = columns.find((c) => c.key === sc.key)
        if (found) {
          return { ...sc, label: found.label, graphql: found.graphql }
        }
        return sc
      })
    }

    loadSavedColumns()
  } catch (error) {
    console.error('Error loading metadata:', error)
  }
}

function loadSavedColumns() {
  const saved = projectStore.loadColumns(STORAGE_KEY.value)
  if (saved) {
    const parsed = [...saved]

    // Update labels from allPossibleColumns if they match
    const updated = parsed.map((p) => {
      const found = allPossibleColumns.value.find((c) => c.key === p.key)
      if (found) {
        return { ...p, label: found.label, graphql: found.graphql }
      }
      return p
    })

    if (!updated.find((c: { key: string }) => c.key === 'fldPrjNr')) {
      updated.unshift({ key: 'fldPrjNr', label: 'Projektnummer', locked: true })
    } else {
      const idx = updated.findIndex((c: { key: string }) => c.key === 'fldPrjNr')
      updated[idx].locked = true
    }
    selectedColumns.value = updated
  } else {
    setDefaultColumns()
  }
}

function setDefaultColumns() {
  const defaults = [
    { key: 'fldPrjNr', label: 'Projektnummer', locked: true },
    { key: 'fldSuchBeg', label: 'Suchbegriff' },
    { key: 'fldBez', label: 'Bezeichnung' },
    { key: 'fldArt', label: 'Projektart' },
    { key: 'fldPrio', label: 'Priorität' },
    { key: 'fldPrjSts', label: 'Projektstatus' },
    { key: 'fldErledigtKz', label: 'Erledigt' },
  ]

  // Use metadata labels if available
  const updatedDefaults = defaults.map((d) => {
    const found = allPossibleColumns.value.find((c) => c.key === d.key)
    if (found) {
      return {
        ...d,
        label: d.label === 'Erledigt' ? 'Erledigt-Kennzeichen' : found.label,
        graphql: found.graphql,
      }
    }
    return d
  })

  selectedColumns.value = updatedDefaults
  projectStore.setColumns(STORAGE_KEY.value, updatedDefaults, false)
}

function resetToDefault() {
  projectStore.removeColumns(STORAGE_KEY.value)
  setDefaultColumns()
  resetAndFetch()
}

function updateColumns(newCols: Array<{ key: string; label: string; locked?: boolean }>) {
  selectedColumns.value = newCols
  projectStore.setColumns(STORAGE_KEY.value, newCols)
  resetAndFetch()
}

function openColumnSelector() {
  portalTable.value?.openColumnSelector()
}

function onRowClick(row: { original: Record<string, unknown> }) {
  const rowData = row.original
  if (!rowData) return
  const prjNr = getValue(rowData, 'fldPrjNr')
  if (prjNr) {
    const isArchive = currentTabIndex.value === 'archive'
    router.push({
      path: `/microtech-hub/projects/${prjNr}`,
      query: { isArchive: isArchive ? 'true' : undefined },
    })
  }
}

watch(currentTabIndex, async () => {
  await fetchMetadata()
  resetAndFetch()
})

function resetAndFetch() {
  projects.value = []
  endCursor.value = ''
  hasNextPage.value = false
  fetchProjects()
}

async function fetchProjects(loadMore = false) {
  if (selectedColumns.value.length === 0 || loading.value) return
  if (loadMore && (!hasNextPage.value || !endCursor.value)) return

  loading.value = true
  try {
    const selectionSet = buildSelectionSet(selectedColumns.value)
    const isArchive = currentTabIndex.value === 'archive'
    const rootField = isArchive ? 'tblProjectsArchive' : 'tblProjects'

    let currentAfter = loadMore && endCursor.value ? endCursor.value : ''
    let moreAvailable = true
    const allFetchedNodes: Record<string, unknown>[] = loadMore ? [...projects.value] : []

    while (moreAvailable) {
      const afterParam = currentAfter ? `after: "${currentAfter}"` : ''
      const query = `
        query {
          ${rootField} {
            conRead(first: ${limit.value}, ${afterParam}) {
              pageInfo {
                endCursor
                hasNextPage
              }
              edges {
                node {
                  ${selectionSet}
                }
              }
            }
          }
        }
      `
      const response = await axios.post('/api/microtech/v1/proxy/graphql', { query })
      const conRead = response.data?.data?.[rootField]?.conRead

      if (!conRead) {
        console.error(`No ${rootField}.conRead data in response:`, response.data)
        moreAvailable = false
        break
      }

      const newBatch = conRead.edges.map((edge: { node: Record<string, unknown> }) => edge.node)
      allFetchedNodes.push(...newBatch)

      currentAfter = conRead.pageInfo.endCursor
      moreAvailable = conRead.pageInfo.hasNextPage

      projects.value = [...allFetchedNodes]
      endCursor.value = currentAfter
      hasNextPage.value = moreAvailable
      totalCount.value = conRead.pageInfo.edgeCount || projects.value.length

      if (newBatch.length === 0) moreAvailable = false
    }
  } catch (error) {
    console.error('Error loading projects:', error)
  } finally {
    loading.value = false
  }
}

function buildSelectionSet(columns: Array<{ key: string; graphql?: string }>) {
  const customSelections = new Set<string>()
  const tree: Record<string, unknown> = {}

  columns.forEach((col) => {
    if (col.graphql && col.graphql.includes('{')) {
      customSelections.add(col.graphql)
    } else {
      const parts = (col.graphql || col.key).split('.')
      let current = tree as Record<string, unknown>
      parts.forEach((part) => {
        if (!current[part]) {
          current[part] = {} as Record<string, unknown>
        }
        current = current[part] as Record<string, unknown>
      })
    }
  })

  function stringify(obj: Record<string, unknown>): string {
    return Object.keys(obj)
      .map((k) => {
        const val = obj[k]
        if (val && typeof val === 'object' && Object.keys(val).length > 0) {
          const sub = stringify(val as Record<string, unknown>)
          return `${k} { ${sub} }`
        }
        return k
      })
      .join('\n')
  }

  const baseSelection = stringify(tree)
  return [baseSelection, ...Array.from(customSelections)].filter((s) => s).join('\n')
}

function getValue(obj: Record<string, unknown>, key: string) {
  return key.split('.').reduce((o: Record<string, unknown> | undefined, i) => {
    if (o && typeof o === 'object' && i in o) {
      return o[i] as Record<string, unknown> | undefined
    }
    return undefined
  }, obj)
}

onMounted(async () => {
  if (customerNo.value && tenantName.value) {
    await fetchMetadata()
    fetchProjects()
  }

  watch([customerNo, tenantName], async ([newCust, newTen]) => {
    if (newCust && newTen) {
      await fetchMetadata()
      fetchProjects()
    }
  })
})
</script>
